import createCase from 'api/createCase';
import getDestinationQualifiers from 'api/getDestinationQualifiers';

import { push } from 'connected-react-router';
import { call, put, select } from 'redux-saga/effects';
import applicationSpinnerActions from 'redux/actions/applicationSpinnerActions';
import caseCreationActions from 'redux/actions/caseCreationActions';
import { hasQuestionsForAccident } from 'redux/sagas/step/step-saga-helper';
import {
  caseCreationSelector,
  decisionTreeResultSelector,
} from 'redux/selectors/caseCreation';
import {
  mapToRenderInLocationSelector,
  selectMoveVersion,
  selectPartnerReference,
  selectTenantId,
  isDestinationFeatureEnabledSelector,
  partnerSelector,
  showConfirmationFollowUpPageSelector,
} from 'redux/selectors/config';
import { routes } from 'Routes/routesConfig';
import { getSynergySessionToken } from 'utils/sagas-utils';

import destinationActions from 'redux/actions/destinationActions';
import { moveToNextStep } from 'redux/actions/stepActions';

const { startLoad, stopLoad } = applicationSpinnerActions;
const { createCaseSuccess, createCaseFail, createSynergyCaseEnd } =
  caseCreationActions;
const { setDestinationQualifiers, setTowingServiceId } = destinationActions;

const NO_DAMAGE = 'NO_DAMAGE';
const DEFAULT_PLACE_TYPE = 'OTHER';

export const getLocationByMapProvider = (mapProvider, currentLocation) => {
  const commonParams = {
    locality: currentLocation?.address?.locality,
    postal_code: currentLocation?.address?.postalCode,
    street_address: currentLocation?.address?.formattedAddress,
  };

  const country =
    mapProvider === 'GOOGLE'
      ? currentLocation?.address?.countryRegionIso2
      : mapProvider === 'BING'
      ? currentLocation?.address?.country
      : undefined;

  return {
    ...commonParams,
    country,
  };
};

export const getAccidentData = data => {
  let vehicle_damage;

  if (data?.type_of_damage) {
    vehicle_damage = data.type_of_damage.toUpperCase();
  } else {
    vehicle_damage = NO_DAMAGE;
  }

  return {
    initial_situation_assessment: {
      is_vehicle_in_mud_or_a_ditch: data?.is_car_in_mud || false,
      has_no_longer_wheels: data?.is_no_longer_on_wheels || false,
      is_police_called: data?.is_police_on_its_way || false,
      is_thirdparty_involved: data?.is_thirdparty_involved || false,
      is_wheels_blocked_or_missing: data?.is_wheels_blocked_or_missing || false,
      is_anyone_injured: false,
      vehicle_damage,
    },
  };
};

export const getCause = decisionTreeResult => {
  if (decisionTreeResult.code) {
    return {
      code: decisionTreeResult.code,
      description: decisionTreeResult.description,
    };
  } else {
    return undefined;
  }
};

export const getPlaceType = currentLocation => {
  if (currentLocation?.is_at_motorway) {
    return 'HIGHWAY';
  } else {
    return DEFAULT_PLACE_TYPE;
  }
};

function* getTowingService(result) {
  if (Array.isArray(result.services)) {
    return yield result.services.find(
      service => service && service.service_code === 'TOWING',
    ) || {};
  }

  return {};
}

function* hasDestinationFeature(status, towingService) {
  const isDestinationFeatureEnabled = yield select(
    isDestinationFeatureEnabledSelector,
  );

  const hasTowingService = towingService =>
    towingService && towingService.service_id;

  return (
    isDestinationFeatureEnabled &&
    [201, 202].includes(status) &&
    hasTowingService(towingService)
  );
}

export const getPolicyHolder = value => {
  if (value === 'yes') {
    return true;
  }

  if (value === 'no') {
    return false;
  }

  return undefined;
};

const getCustomerData = (
  isPolicyHolder,
  personName,
  personSurname,
  phoneCountryCodeContact,
  phoneNumberContact,
) => {
  const isPolicyHolderParsed = getPolicyHolder(isPolicyHolder);
  const contactPhone = {
    international_prefix: phoneCountryCodeContact,
    number: phoneNumberContact,
  };

  if (isPolicyHolderParsed) {
    return {
      contact_phone: contactPhone,
      is_policy_holder: isPolicyHolderParsed,
    };
  }

  return {
    contact_phone: contactPhone,
    first_name: personName,
    is_policy_holder: isPolicyHolderParsed,
    last_name: personSurname,
  };
};

export const getServiceAppointment = serviceAppointment => {
  const hasAppointment = serviceAppointment !== undefined;

  const bodyWithNoDate = { is_scheduled: hasAppointment };

  if (hasAppointment) {
    return { ...bodyWithNoDate, scheduled_at: serviceAppointment };
  } else {
    return bodyWithNoDate;
  }
};

export const getSpecialRequirements = (passengersDetails = {}) => {
  const hasPassengersDetails = Object.keys(passengersDetails).length > 0;

  if (hasPassengersDetails) {
    const normalizedRequirements = JSON.stringify(passengersDetails);
    return {
      special_requirements: normalizedRequirements,
    };
  }
  return {};
};

function* getCaseData() {
  const partnerReference = yield select(selectPartnerReference);
  const mapProvider = yield select(mapToRenderInLocationSelector);
  const decisionTreeResult = yield select(decisionTreeResultSelector);
  const isAccident = hasQuestionsForAccident(decisionTreeResult);
  const {
    accident,
    currentLocation,
    isPolicyHolder,
    passengers,
    personName,
    personSurname,
    phoneCountryCodeContact,
    phoneNumberContact,
    plateNumber,
    serviceAppointment,
  } = yield select(caseCreationSelector);

  return {
    cause: getCause(decisionTreeResult),
    vehicle: {
      registration_number: plateNumber,
      gearbox_type: 'AUTO',
      fuel_type: 'LIQUID_PETROL_GAS',
    },
    customer: getCustomerData(
      isPolicyHolder,
      personName,
      personSurname,
      phoneCountryCodeContact,
      phoneNumberContact,
    ),
    current_location: {
      address: { ...getLocationByMapProvider(mapProvider, currentLocation) },
      coordinates: currentLocation?.coordinates,
      has_a_height_restriction: currentLocation?.has_a_height_restriction,
      additional_information: currentLocation?.additional_information,
      place_type: getPlaceType(currentLocation),
    },
    partner_reference: partnerReference,
    passengers: {
      children_number: passengers?.childrenNumber || 0,
      passengers_number: passengers?.passengersNumber,
    },
    service_appointment: getServiceAppointment(serviceAppointment),
    ...getSpecialRequirements(passengers?.details),
    ...(isAccident ? getAccidentData(accident) : {}),
  };
}

function* handleDestinationFeature({
  caseReference,
  moveVersion,
  sessionToken,
  tenantId,
  towingService,
}) {
  yield put(setTowingServiceId({ serviceId: towingService.service_id }));

  const response = yield call(getDestinationQualifiers, {
    caseId: caseReference,
    serviceId: towingService.service_id,
    token: sessionToken,
    tenantId,
    moveVersion,
  });
  const { result: responseDestination, status: statusDestination } = response;

  if (statusDestination === 200) {
    yield put(
      setDestinationQualifiers({
        choices: responseDestination,
      }),
    );
    yield put(moveToNextStep());
  } else {
    yield put(push(routes.CALLBACK));
  }
}

export function* createSynergyCaseSaga() {
  try {
    yield put(startLoad());

    const tenantId = yield select(selectTenantId);
    const moveVersion = yield select(selectMoveVersion);
    const partner = yield select(partnerSelector);

    const isSuccessCase = status => [200, 201, 202].includes(status);

    const caseData = yield getCaseData();

    const sessionToken = yield getSynergySessionToken();

    const {
      result: { case_guid: caseGuid, case_id: caseReference, ...result },
      status,
    } = yield call(createCase, {
      body: caseData,
      token: sessionToken,
      tenantId,
      moveVersion,
      partner,
    });

    if (isSuccessCase(status)) {
      const shouldRedirectToConfirmationFollowUp = yield select(
        showConfirmationFollowUpPageSelector,
      );
      yield put(createCaseSuccess({ caseGuid, caseReference }));

      const towingService = yield getTowingService(result);
      const hasDestinationFeatureEnabled = yield hasDestinationFeature(
        status,
        towingService,
      );
      if (hasDestinationFeatureEnabled) {
        return yield handleDestinationFeature({
          caseReference,
          moveVersion,
          sessionToken,
          tenantId,
          towingService,
        });
      }
      yield put(createSynergyCaseEnd());

      if (status === 201 && shouldRedirectToConfirmationFollowUp) {
        yield put(push(routes.SYNERGY_FOLLOW_CONFIRMATION));
        return;
      }

      yield put(push(status === 201 ? routes.CONFIRMATION : routes.CALLBACK));
    } else {
      yield put(createCaseFail(result));
      yield put(push(routes.CONTACT_US));
    }
  } catch (error) {
    yield put(createCaseFail(error?.message || error));
    yield put(push(routes.CONTACT_US));
  } finally {
    yield put(stopLoad());
  }
}
